import { useCallback, useState } from "preact/hooks";
import type { SubmissionDocRequirement } from "../data/laCountySubmissionCatalog";
import { resolveUploadFolderId } from "../lib/resolveUploadFolder";
import {
  readFileAsBase64,
  validateSubmissionDocument,
} from "../lib/validateSubmissionDocument";
import { useAccBrowseStore } from "../stores/accBrowseStore";
import { useDocumentViewerStore } from "../stores/documentViewerStore";
import { useSubmissionUploadStore } from "../stores/submissionUploadStore";
import { useDataManagementService } from "./useDataManagementService";

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function animateProgress(
  from: number,
  to: number,
  ms: number,
  onTick: (value: number) => void
): Promise<void> {
  const steps = Math.max(1, Math.round(ms / 80));
  for (let i = 1; i <= steps; i += 1) {
    const value = from + ((to - from) * i) / steps;
    onTick(value);
    await sleep(ms / steps);
  }
}

async function validatePdfForRequirement(
  requirement: SubmissionDocRequirement,
  file: File
): Promise<void> {
  const fileBase64 = await readFileAsBase64(file);
  const validation = await validateSubmissionDocument({
    requirementId: requirement.id,
    requirementLabel: requirement.label,
    validationCriteria: requirement.validationCriteria,
    fileName: file.name,
    fileBase64,
  });

  if (validation.verdict === "reject") {
    const detail = validation.issues?.join(" ") ?? validation.summary;
    throw new Error(detail || "Document did not pass validation.");
  }
}

export function useSubmissionUpload(onUploaded: () => Promise<void>) {
  const dm = useDataManagementService();
  const [lastError, setLastError] = useState<string | null>(null);

  const runScanWithProgress = useCallback(
    async (
      uploadStore: ReturnType<typeof useSubmissionUploadStore.getState>
    ) => {
      uploadStore.setPhase("scanning");
      await animateProgress(78, 92, 350, uploadStore.setProgress);
      await onUploaded();
      uploadStore.setProgress(100);
    },
    [onUploaded]
  );

  const uploadForRequirement = useCallback(
    async (
      requirement: SubmissionDocRequirement,
      file: File,
      options?: { replaceItemId?: string }
    ) => {
      const hub = useAccBrowseStore.getState().hub;
      const project = useAccBrowseStore.getState().project;
      const activeFolderId = useAccBrowseStore.getState().activeFolderId;
      if (!hub || !project) {
        throw new Error("Select an ACC hub and project first.");
      }

      setLastError(null);
      const uploadStore = useSubmissionUploadStore.getState();
      uploadStore.start(requirement.id);
      uploadStore.setProgress(5);

      try {
        await animateProgress(5, 22, 400, uploadStore.setProgress);
        uploadStore.setPhase("validating");
        await validatePdfForRequirement(requirement, file);

        uploadStore.setProgress(32);
        uploadStore.setPhase("uploading");
        await animateProgress(32, 55, 300, uploadStore.setProgress);

        const prefix =
          requirement.matchKeywords[0] ?? requirement.id.replace(/-/g, "_");
        const uploadName = `${prefix}-${file.name.replace(/\s+/g, "_")}`;
        const pdfFile = new File([file], uploadName, {
          type: file.type || "application/pdf",
        });

        if (options?.replaceItemId) {
          await dm.uploadNewVersionToItem(
            project.id,
            options.replaceItemId,
            pdfFile,
            hub.region
          );
        } else {
          const folderId = await resolveUploadFolderId(
            dm,
            hub,
            project,
            activeFolderId
          );
          await dm.uploadFileToFolder(
            project.id,
            folderId,
            pdfFile,
            hub.region
          );
        }

        uploadStore.setProgress(78);
        await runScanWithProgress(uploadStore);
        uploadStore.markDone(requirement.id);
      } catch (e) {
        uploadStore.reset();
        const msg = e instanceof Error ? e.message : "Upload failed";
        setLastError(msg);
        throw e;
      }
    },
    [dm, runScanWithProgress]
  );

  const removeMatchedPdf = useCallback(
    async (
      requirement: SubmissionDocRequirement,
      itemId: string,
      fileName: string
    ) => {
      const hub = useAccBrowseStore.getState().hub;
      const project = useAccBrowseStore.getState().project;
      if (!hub || !project) {
        throw new Error("Select an ACC hub and project first.");
      }

      setLastError(null);
      const uploadStore = useSubmissionUploadStore.getState();
      uploadStore.start(requirement.id);
      uploadStore.setProgress(10);
      uploadStore.setPhase("removing");

      try {
        await animateProgress(10, 65, 400, uploadStore.setProgress);
        await dm.softDeleteProjectItem(
          project.id,
          itemId,
          fileName,
          hub.region
        );

        const openDoc = useDocumentViewerStore.getState().openDoc;
        if (
          openDoc?.itemId === itemId ||
          openDoc?.itemId.startsWith(itemId.split("?")[0] ?? "")
        ) {
          useDocumentViewerStore.getState().close();
        }

        uploadStore.setProgress(72);
        await runScanWithProgress(uploadStore);
        uploadStore.reset();
      } catch (e) {
        uploadStore.reset();
        const msg = e instanceof Error ? e.message : "Remove failed";
        setLastError(msg);
        throw e;
      }
    },
    [dm, runScanWithProgress]
  );

  const activeRequirementId = useSubmissionUploadStore(
    s => s.activeRequirementId
  );
  const progress = useSubmissionUploadStore(s => s.progress);
  const phase = useSubmissionUploadStore(s => s.phase);
  const justCompletedIds = useSubmissionUploadStore(s => s.justCompletedIds);

  return {
    uploadForRequirement,
    removeMatchedPdf,
    activeRequirementId,
    progress,
    phase,
    justCompletedIds,
    lastError,
    busy:
      activeRequirementId !== null || (phase !== "idle" && phase !== "done"),
  };
}
